import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
  DeleteIconWrapper
} from './styles';
import CustomPagination from '../../../../components/CustomPagination';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useSidebar } from '../../../../context/SidebarContext';
import DocumentTypeModal from './components/DocumentTypeModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { 
  fetchSuperAdminDocumentTypes, 
  deleteSuperAdminDocumentType 
} from '../../../../modules/superadmin/actions';
import { 
  resetSuperAdminActionState 
} from '../../../../modules/superadmin/reducers';
import { DocumentType } from '../../../../modules/superadmin/types';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../../components/common/AlertPopup';

const SuperAdminDocumentTypes: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const {
    documentTypes,
    documentTypesCount: totalCount,
    documentTypesLoading: loading,
    createDocumentTypeSuccess: createSuccess,
    createDocumentTypeError: createError,
    updateDocumentTypeSuccess: updateSuccess,
    updateDocumentTypeError: updateError,
    deleteDocumentTypeSuccess: deleteSuccess,
    deleteDocumentTypeError: deleteError,
    actionMessage
  } = useSelector((state: RootState) => state.superadmin);

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(null);

  // Confirmation Dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('document-types');
    setPath([
      { title: 'Master Data', path: '/super-admin/masterdata/document-types' },
      { title: 'Document Types', path: '/super-admin/masterdata/document-types' },
    ]);
    setIsShow(true);
    dispatch(fetchSuperAdminDocumentTypes({ search: '', page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);

  // Handle Notifications and Resets
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: actionMessage || 'Document Type created successfully', severity: 'success' });
      dispatch(fetchSuperAdminDocumentTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetSuperAdminActionState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetSuperAdminActionState());
    }

    if (updateSuccess) {
      setAlert({ message: actionMessage || 'Document Type updated successfully', severity: 'success' });
      dispatch(fetchSuperAdminDocumentTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetSuperAdminActionState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetSuperAdminActionState());
    }

    if (deleteSuccess) {
      setAlert({ message: actionMessage || 'Document Type deleted successfully', severity: 'success' });
      dispatch(fetchSuperAdminDocumentTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetSuperAdminActionState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetSuperAdminActionState());
    }
  }, [createSuccess, createError, updateSuccess, updateError, deleteSuccess, deleteError, dispatch, page, actionMessage]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    dispatch(fetchSuperAdminDocumentTypes({ search: '', page: newPage, page_size: PAGE_SIZE }));
  };

  const handleCreateClick = () => {
    setSelectedDocType(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (docType: DocumentType) => {
    setSelectedDocType(docType);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteSuperAdminDocumentType(deleteId));
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  return (
    <Box sx={{ p: 3 }}>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Document Types (Master Data)</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Document Type
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Req Expiry</StyledTableCell>
                <StyledTableCell>Req Issue</StyledTableCell>
                <StyledTableCell>Is Mandatory</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {documentTypes && documentTypes.length > 0 ? (
                documentTypes.map((docType: DocumentType) => (
                  <StyledTableRow key={docType.document_type_id}>
                    <StyledTableCell>{docType.document_type_id}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600 }}>{docType.document_type}</StyledTableCell>
                    <StyledTableCell>{docType.description || '-'}</StyledTableCell>
                    <StyledTableCell>{docType.is_expiry_required ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{docType.is_issue_date_required ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{docType.is_mandatory ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(docType)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                        <DeleteIconWrapper onClick={() => handleDeleteClick(docType.document_type_id)}>
                          <DeleteIcon fontSize="small" />
                        </DeleteIconWrapper>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={7} align="center">
                    {loading ? 'Loading...' : 'No document types found.'}
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <DocumentTypeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        documentType={selectedDocType}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document Type"
        message="Are you sure you want to delete this document type from Master Data?"
      />
    </Box>
  );
};

export default SuperAdminDocumentTypes;
